import { useTagTheme } from '@/hooks/useTagTheme'
import { TagHeader } from '@/components/TagHeader'
import { PostList } from '@/components/PostList'
import { AddPostCta } from '@/components/AddPostCta'
import type { Tag, Post, PostStop, ResolvedTheme } from '@/types'

/**
 * DEV-ONLY visual harness for the Tag Page redesign, served at `/__preview/tag`
 * (registered only when `import.meta.env.DEV`, so it never ships). It renders the
 * real Tag Page components against mock posts so the redesign can be reviewed
 * without seeding Firebase. Firestore reads for the mock ids resolve empty
 * (no owner photo, empty reactions), which is fine for a styling preview.
 */
const stop = (
  showName: string,
  city: string,
  venue: string | null = null,
): PostStop => ({ showId: null, showName, venue, city })

const mkPost = (p: Partial<Post> & { postId: string }): Post => ({
  tagId: 'b1',
  userId: p.postId,
  userHandle: 'raver',
  isAnonymous: false,
  alias: null,
  deviceToken: null,
  mediaUrl: null,
  mediaType: null,
  mediaStatus: 'ready',
  thumbnailUrl: null,
  message: '',
  hidden: false,
  createdAt: new Date(),
  ...p,
})

const POSTS: Post[] = [
  mkPost({
    postId: 'p1',
    userHandle: 'basshead_bri',
    stop: stop('Lost Lands', 'Legend Valley, OH'),
    mediaType: 'photo',
    mediaUrl: 'https://picsum.photos/seed/sprout/900/560',
    message:
      "this sprout has been EVERYWHERE and somehow found its way to me 🍄 traded for my last tag at the Subtronics set. it's home now ✌️",
  }),
  mkPost({
    postId: 'p2',
    userHandle: 'plur_pixie',
    stop: stop('EDC Las Vegas', 'Las Vegas, NV', 'kineticFIELD'),
    mediaType: 'photo',
    mediaUrl: 'https://picsum.photos/seed/pixie/520/780',
    message:
      'sunrise set and a total stranger pressed this into my hand 💗 cried a lil ngl. PLUR is real',
  }),
  mkPost({
    postId: 'p3',
    userHandle: 'wook_witch',
    stop: stop('Excision: Thunderdome', 'Tacoma, WA'),
    message: 'headbanged so hard it almost flew off 🤘 worth every neck cramp',
  }),
  mkPost({
    postId: 'p4',
    userHandle: 'deep_end_dani',
    stop: stop('Electric Forest', 'Sherwood, MI'),
    message: 'passed it on under the disco trees 🌲✨ keep it moving fam',
  }),
  mkPost({
    postId: 'p5',
    userHandle: 'kandiwooks',
    stop: stop('Minted', 'Seattle, WA', 'the workshop'),
    message: 'hand-printed + strung with love. go make some memories 💚 — Jake & Tay',
  }),
]

const TAG: Tag = {
  tagId: 'b1',
  ownerId: null,
  ownerClaimed: true,
  provisioned: true,
  name: 'TOXIC SPROUT',
  ownerTheme: null,
  themeId: null,
  variant: 'plur',
  totalTaps: 14,
  createdAt: new Date(),
}

const THEME: ResolvedTheme = {
  accentColor: '#a3e635',
  primaryColor: '#22d3ee',
  backgroundStyle: 'gradient',
  backgroundAsset: null,
  logoUrl: null,
  motif: '🍄',
  pinnedMessage: 'trade me far & wide, then bring me home. show me the bass 🍄💚',
  sourceThemeId: null,
}

export const DevTagPreview = () => {
  const { style } = useTagTheme(THEME)
  return (
    <div className="relative min-h-dvh overflow-hidden" style={style}>
      <div
        className="pointer-events-none fixed inset-x-0 top-[-40px] z-0 flex justify-center overflow-hidden text-[230px] leading-none opacity-5"
        aria-hidden="true"
      >
        🍄
      </div>
      <div className="relative z-[1] mx-auto flex w-full max-w-[468px] flex-col gap-5 px-4 pb-[132px] pt-2">
        <TagHeader tag={TAG} theme={THEME} posts={POSTS} />
        <PostList posts={POSTS} viewerId={null} />
      </div>
      <AddPostCta tagId="t1" />
    </div>
  )
}
